import React, { useCallback, useMemo } from 'react';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { mkConfig, generateCsv, download } from 'export-to-csv';
import {
  CheckCircle2Icon,
  CircleMinusIcon,
  FileSpreadsheet,
} from 'lucide-react';
import { getGetBuslineImportEntriesQueryOptions } from '@area-restrita/api-client/endpoints/busline-imports/busline-imports.gen';
import { GetBuslineImportEntriesStatus } from '@area-restrita/api-client/model/getBuslineImportEntriesStatus';
import { BuslineImportEntryDto } from '@area-restrita/api-client/model/buslineImportEntryDto';
import { BuslineImportDto } from '@area-restrita/api-client/model/buslineImportDto';

import DataGrid from '@/components/DataGrid';
import { Button } from '@/components/ui/button';
import { useQueryTable } from '@/hooks/useQueryTable';

const DayCell = ({ getValue }: CellContext<BuslineImportEntryDto, unknown>) =>
  getValue() ? (
    <span className="w-full flex justify-center">
      <CheckCircle2Icon className="text-green-600" />
    </span>
  ) : (
    <span className="w-full flex justify-center">
      <CircleMinusIcon className="text-red-600" />
    </span>
  );

const validColumns: ColumnDef<BuslineImportEntryDto>[] = [
  { accessorKey: 'id', header: 'ID' },
  { accessorKey: 'code', header: 'Linha' },
  { accessorKey: 'prefix', header: 'Prefixo' },
  { accessorKey: 'startAt', header: 'Início' },
  { accessorKey: 'endAt', header: 'Fim' },
  {
    accessorKey: 'sun',
    header: 'Dom',
    meta: { className: 'text-center' },
    cell: DayCell,
  },
  {
    accessorKey: 'mon',
    header: 'Seg',
    meta: { className: 'text-center' },

    cell: DayCell,
  },
  {
    accessorKey: 'tue',
    header: 'Ter',
    meta: { className: 'text-center' },

    cell: DayCell,
  },
  {
    accessorKey: 'wed',
    header: 'Qua',
    meta: { className: 'text-center' },

    cell: DayCell,
  },
  {
    accessorKey: 'thu',
    header: 'Qui',
    meta: { className: 'text-center' },

    cell: DayCell,
  },
  {
    accessorKey: 'fri',
    header: 'Sex',
    meta: { className: 'text-center' },

    cell: DayCell,
  },
  {
    accessorKey: 'sat',
    header: 'Sáb',
    meta: { className: 'text-center' },
    cell: DayCell,
  },
  {
    accessorKey: 'driverExternalId',
    header: 'ID Externo do Motorista',
    cell: ({ getValue }) => getValue() || '-',
  },
  {
    accessorKey: 'checklistName',
    header: 'Checklist',
    cell: ({ getValue }) => getValue() || '-',
  },
];

const invalidColumns: ColumnDef<BuslineImportEntryDto>[] = [
  ...validColumns,
  { accessorKey: 'invalidReason', header: 'Motivo' },
];

interface IProps {
  buslineImport: BuslineImportDto;
  status: GetBuslineImportEntriesStatus;
}

const EntriesTableDialog: React.FC<IProps> = ({ buslineImport, status }) => {
  const columns = useMemo(
    () => (status === 'valid' ? validColumns : invalidColumns),
    [status],
  );

  const csvConfig = useMemo(
    () =>
      mkConfig({
        fieldSeparator: ';',
        filename: `${buslineImport.scheduledDate}_importacao_linhas_onibus_${status === 'valid' ? 'validas' : 'invalidas'}`,
        decimalSeparator: '.',
        quoteStrings: false,
        // useKeysAsHeaders: true,
        columnHeaders: columns.slice(1).map((column) => {
          const key = 'accessorKey' in column ? column.accessorKey : column.id;

          if (!key) throw new Error('Coluna sem identificador para exportação');

          return {
            key: String(key),
            displayLabel: String(column.header),
          };
        }),
      }),
    [columns, status, buslineImport.scheduledDate],
  );

  const {
    query: { isLoading },
    table,
  } = useQueryTable({
    columns,
    getQueryOptions: (args) =>
      getGetBuslineImportEntriesQueryOptions(buslineImport.id, {
        status,
        ...args,
      }),
  });

  const handleExport = useCallback(() => {
    const rowData = table.getRowModel().rows.map((row) => row.original);

    const csv = generateCsv(csvConfig)(
      rowData as unknown as Record<string, string>[],
    );

    download(csvConfig)(csv);
  }, [csvConfig, table]);

  return (
    <DataGrid
      className="overflow-y-auto max-h-[70vh]"
      variant="card"
      disablePagination
      table={table}
      isLoading={isLoading}
      actions={
        <Button size="sm" onClick={handleExport}>
          <FileSpreadsheet /> Exportar CSV
        </Button>
      }
    />
  );
};

export default EntriesTableDialog;
